/**
 * aiEventSink — el ÚNICO intérprete de eventos de ARIA en el cliente.
 *
 * POR QUÉ EXISTE. Hay dos transportes que traen exactamente los mismos objetos:
 *
 *   1. SSE (`useAIStream`)  — cada línea `data: {...}` del stream.
 *   2. Run durable (`useAriaRun`) — el `payload` de cada fila de `run_events`,
 *      que el worker persiste YA TRADUCIDO al dialecto del cliente con la misma
 *      función que usa el SSE (`runExecutor.ts`: `streamEventToClient(e) ?? e`).
 *      Antes se persistía el evento crudo del grafo y de todo el turno sólo
 *      llegaba `text`, por casualidad; lo arregló api#1601.
 *
 * El objeto es el mismo; sólo cambia por dónde llega. Escribir un segundo
 * intérprete sería repetir en el cliente la divergencia que la auditoría
 * documentó en el backend (H14: dos pipelines que se separan en features sin
 * que nadie lo note). Con un sink único, un evento nuevo se entiende en ambos
 * caminos o en ninguno.
 *
 * Es deliberadamente puro: sin React, sin fetch, sin timers. Acumula el estado
 * que el transporte necesita al cerrar (`fullText`, `toolsUsed`, `chart`…) y
 * reenvía cada evento a su callback. Los callbacks son todos opcionales.
 */

/**
 * Pega la referencia de soporte (el requestId del backend) al texto de un error de ARIA, con el MISMO formato que los toasts
 * HTTP (`lib/query-client.ts`: «Ref: xxxxxxxx»). Un error a mitad de stream no tiene cabeceras que leer: el backend manda el
 * `ref` en el propio evento. Sin ref, el texto queda igual.
 */
export function conReferencia(mensaje, ref) {
  return typeof ref === 'string' && ref ? `${mensaje} · Ref: ${ref.slice(0, 8)}` : mensaje;
}

/**
 * ¿Es el texto previo a una tool una frase de trámite («Voy a consultar el
 * saldo y las nóminas.»)? Corta y sin estructura markdown: una tabla, un título
 * o una lista son contenido y no se apartan nunca de la respuesta, y tampoco un
 * texto con un importe (el backend lo necesita para comprobar las cifras).
 */
export function isNarration(text) {
  return Boolean(text) && text.length <= 300
    && !/\n\s*(?:[|#>*-]|\d+\.)|\|/.test(text)
    && !/\d[\d.,]*\s*(?:€|eur)/i.test(text);
}

/**
 * @param {object} callbacks — mismo contrato que `useAIStream`.
 * @returns {{ apply: (data: any) => void, state: object }}
 */
export function createAIEventSink(callbacks = {}) {
  const {
    onText, onRouting, onToolThinking, onToolFinish,
    onActionResult, onChart, onGroundingWarning, onPlan, onThinkingText,
    onAgentStart, onAgentFinish, onConversationId, onUsage,
    // E5 — razonamiento visible: la pizarra llenándose y las regeneraciones.
    onFact, onRevision, onAnswerConfidence,
    onHrOptInRequired,
    /** Tope de plan alcanzado (`PLAN_QUOTA_EXCEEDED`): lo pinta el modal, no el chat. */
    onPlanLimit,
  } = callbacks;

  // Todo el texto recibido, y desde dónde empieza la respuesta. El modelo suele
  // anunciar lo que va a consultar («Voy a consultar el saldo…») antes de
  // llamar a las tools, y ese texto llega por el mismo evento `text` que la
  // respuesta: concatenado, quedaba como primera línea del cuerpo y pegado a
  // ella («…completa.## ¿Podrás…», con el `##` saliendo literal). Ahora cada
  // arranque de tool cierra el tramo anterior: si parece una frase de trámite
  // va a la zona de razonamiento por `onThinkingText` y el cuerpo empieza de
  // nuevo; si es contenido (una tabla antes de proponer una factura) se queda
  // en el cuerpo, separado por una línea en blanco de lo que venga después.
  // Misma regla que el backend al persistir (verix-api `chatHandler.ts`).
  let rawText = '';
  let bodyStart = 0;
  let needsBreak = false;

  const state = {
    /**
     * La respuesta visible: el texto posterior a la última tool. Si ese tramo
     * queda vacío (el modelo respondió ANTES de una tool de acción) se devuelve
     * todo, para no perder nunca contenido.
     */
    get fullText() {
      const body = rawText.slice(bodyStart);
      return body.trim() ? body.replace(/^\s+/, '') : rawText;
    },
    toolsUsed: [],
    /** Detalles del tope alcanzado, o null. `{ resource, usage, limit }`. */
    planLimit: null,
    /** `_agent` del evento done: { domain, cross_domains, model_tier, … } */
    agentMeta: null,
    pendingActions: [],
    pendingClarifications: [],
    chart: null,
    groundingWarning: null,
    /**
     * E1 — confianza CALCULADA de la respuesta: `{ confidence, caveats, signals }`.
     *
     * El backend la computa con señales objetivas del turno (un especialista que
     * reventó, un hueco declarado, una tool sin datos, cifras que el juez marcó
     * sin respaldo) en vez de preguntarle al modelo, que es mal calibrador de sí
     * mismo. Se emitía desde el fan-out y el cliente no la leía en ningún sitio:
     * `answerConfidence` tenía CERO referencias en todo el frontal.
     *
     * Sólo la emite el fan-out (`finalizeNode`); en un turno single-agent queda
     * `null` y no se pinta nada.
     */
    answerConfidence: null,
    /** ¿llegó el evento terminal? Su ausencia significa respuesta truncada. */
    sawDone: false,
    /**
     * XC-AI-013 — el gate de sesgo de RRHH cortocircuitó el turno.
     *
     * Sólo se manejaba en la rama HTTP plana, porque se asumía que el gate no
     * podía darse sobre un stream. Sí podía: `prepareKernelPlan` corre en los
     * DOS transportes. En producción eso reventaba el backend (4 turnos entre
     * el 2026-07-20 y el 07-25). Con el backend arreglado el frame llega por
     * SSE, y sin esto se ignoraría en silencio — el mismo spinner muerto por
     * otra vía.
     */
    hrOptInRequired: false,
    /** Error que el backend marcó reintentable — no es una respuesta. */
    retryableError: null,
  };

  function apply(data) {
    if (!data || typeof data !== 'object') return;

    if (data.text) {
      if (needsBreak && !/\n$/.test(rawText) && !/^\n/.test(data.text)) rawText += '\n\n';
      needsBreak = false;
      rawText += data.text;
      onText?.(data.text, rawText.slice(bodyStart).replace(/^\s+/, ''));
    }
    if (data.routing) onRouting?.(data.routing);
    if (data.plan) onPlan?.(data.plan);
    if (data.agentStart) onAgentStart?.(data.agentStart);
    if (data.agentFinish) onAgentFinish?.(data.agentFinish);
    // E5 — un hecho entrando en la pizarra. El backend NO manda el valor (los
    // importes del tenant no viajan en un evento que además se persiste), así
    // que aquí sólo hay identidad y procedencia: agente, dominio y etiqueta.
    if (data.hrOptInRequired) {
      state.hrOptInRequired = true;
      onHrOptInRequired?.();
    }
    if (data.fact) onFact?.(data.fact);
    // E5 — el sintetizador rehaciendo la respuesta. Explica la espera larga.
    if (data.revision) onRevision?.(data.revision);
    if (data.answerConfidence) {
      state.answerConfidence = data.answerConfidence;
      onAnswerConfidence?.(data.answerConfidence);
    }

    // `thinking` viaja en dos formas distintas sobre la misma clave: como STRING
    // es la narración del supervisor; como `true` acompañado de `tool` es el
    // arranque de una herramienta. Se desambigua por tipo, no por orden.
    if (typeof data.thinking === 'string' && data.thinking) onThinkingText?.(data.thinking);
    if (data.thinking && data.tool) {
      const pending = rawText.slice(bodyStart).trim();
      if (isNarration(pending)) {
        bodyStart = rawText.length;
        onThinkingText?.(pending);
        onText?.('', '');
      } else if (pending) {
        needsBreak = true;
      }
      onToolThinking?.(data.tool, state.fullText, data.domain);
    }

    if (data.toolFinish?.tool) onToolFinish?.(data.toolFinish, state.fullText);

    // onPendingAction/onPendingClarification NO existen como callback — sin
    // consumidor real (auditoría 2026-08-04): AriaConversationContext lee el
    // agregado `pendingActions`/`pendingClarifications` que ya viaja dentro
    // de `onDone`, no evento a evento. Solo se acumula en `state` aquí.
    if (data.pendingAction) state.pendingActions.push(data.pendingAction);
    if (data.pendingClarification) state.pendingClarifications.push(data.pendingClarification);
    if (data.actionResult) onActionResult?.(data.actionResult);

    if (data.chart) {
      state.chart = data.chart;
      onChart?.(data.chart);
    }
    if (data.grounding_warning) {
      state.groundingWarning = data.grounding_warning;
      onGroundingWarning?.(data.grounding_warning);
    }

    if (data.done) {
      state.sawDone = true;
      if (data.toolsUsed) state.toolsUsed = data.toolsUsed;
      if (data._agent) state.agentMeta = data._agent;
      if (data.conversationId) onConversationId?.(data.conversationId);
      if (data.usage) onUsage?.(data.usage);
    }

    if (data.error) {
      // Tope de plan alcanzado: NO se pega el texto en la conversación. Va al
      // mismo modal de límite que ya abre cualquier otra cuota (facturas, OCR,
      // bancos, asientos), que es donde el usuario espera encontrarlo.
      //
      // Hasta ahora este caso no llegaba nunca: el modal lo dispara el
      // MutationCache de React Query, y el error de ARIA viaja por SSE, que no
      // pasa por ahí. El usuario veía «Error: Límite diario…» concatenado al
      // final de la respuesta, como si fuera parte de lo que dijo ARIA.
      //
      // Se avisa por callback y no llamando al store aquí: este módulo es puro a
      // propósito (sin React, sin fetch, sin timers) y el transporte es quien
      // sabe de stores.
      if (data.code === 'PLAN_QUOTA_EXCEEDED') {
        state.planLimit = data.details ?? {};
        onPlanLimit?.(data.details ?? {});
        return;
      }
      // Reintentable: el componente ofrece "Reintentar" en vez de presentar el
      // fallo como si fuera la respuesta. No reintentable: se concatena, que es
      // lo que el usuario necesita leer.
      if (data.retryable) state.retryableError = { message: conReferencia(data.error, data.ref) };
      else rawText += '\n\nError: ' + conReferencia(data.error, data.ref);
    }
  }

  return { apply, state };
}

export default createAIEventSink;
